import { useEffect, useState } from 'react';
import { useApiDownDetection } from '@/hooks/useApiDownDetection';
import { detectBeTargetEnvFromBrowser } from '@/lib/config/runtimeEnv';
import { useI18nText } from '@/lib/utils/i18nText';

const BE_DOMAINS: Record<ReturnType<typeof detectBeTargetEnvFromBrowser>, string> = {
  staging: 'api-staging.ggcontent.com',
  live: 'api.ggcontent.com',
};

const ENV_LABELS: Record<ReturnType<typeof detectBeTargetEnvFromBrowser>, { en: string; ko: string }> = {
  staging: { en: 'Staging', ko: '스테이징' },
  live: { en: 'Production', ko: '프로덕션' },
};

export default function ApiDownBanner() {
  const { isDown } = useApiDownDetection();
  const { t } = useI18nText();
  // Dismissing only hides this occurrence — it doesn't touch the underlying counter, so
  // if the BE recovers and later goes down again (a fresh isDown: false → true edge),
  // the banner reappears rather than staying permanently dismissed.
  const [dismissed, setDismissed] = useState(false);
  useEffect(() => {
    if (!isDown) setDismissed(false);
  }, [isDown]);

  if (!isDown || dismissed) return null;

  const beEnv = detectBeTargetEnvFromBrowser();
  const domain = BE_DOMAINS[beEnv];
  const envLabel = t(ENV_LABELS[beEnv].en, ENV_LABELS[beEnv].ko);

  return (
    <div className="api-down-banner" role="alert" aria-live="assertive">
      <span className="api-down-banner__icon">
        <i className="fa fa-exclamation-triangle" aria-hidden="true" />
      </span>
      <span className="api-down-banner__body">
        <span className="api-down-banner__title">
          {t(
            `BE v2 API (${envLabel}) is unavailable`,
            `BE v2 API (${envLabel}) 서버가 응답하지 않습니다`,
          )}
        </span>
        <span className="api-down-banner__domain">{domain}</span>
        <span className="api-down-banner__desc">
          {t(
            'Please report this to the development team so they can restart or restore the server.',
            '서버를 재시작 또는 조치를 취할 수 있도록 개발 담당자에게 보고하세요.',
          )}
        </span>
      </span>
      <span className="api-down-banner__checking">
        <i className="fa fa-circle-o-notch api-down-banner__spin" aria-hidden="true" />
        {t('Checking recovery…', '복구 확인 중…')}
      </span>
      <button
        type="button"
        className="api-down-banner__close"
        aria-label={t('Dismiss', '닫기')}
        onClick={() => setDismissed(true)}
      >
        <i className="fa fa-times" aria-hidden="true" />
      </button>
    </div>
  );
}
